import { useEffect, useSyncExternalStore } from "react";
import {
  LocalContextSelection,
  LocalContextResolution,
  type ContextSelectionExpression,
} from "@cleo/contracts";
import * as Schema from "effect/Schema";
import { parseFilesystemMemberReference } from "@cleo/shared/contextSelection";
import { agentContextResolutionDigestMatches } from "@cleo/shared/agentContextResolution";
import { orderSessionSelection } from "./sessionSelectionOrder";
import { newCommandId } from "../lib/utils";
import { fetchPrimary } from "../environments/primary/fetch";

export interface LocalBoundaryMember {
  readonly id: string;
  readonly label: string;
  readonly kind?: "member" | "folder";
  readonly ancestors?: ReadonlyArray<string>;
}

export type LocalBoundaryRead =
  | { readonly state: "loading" }
  | { readonly state: "unavailable"; readonly reason: string }
  | {
      readonly state: "members";
      readonly root: string;
      readonly nextCursor?: string | null;
      readonly members: ReadonlyArray<LocalBoundaryMember>;
      /** Non-null when the walk refused past this many members (overflow). */
      readonly truncatedAt: number | null;
      /** The walk's own overflow prose, when truncated. */
      readonly reason: string | null;
    };

/** Fold the endpoint's answer into the read the rail renders. Pure. */
export function projectLocalBoundaryResponse(body: unknown): LocalBoundaryRead {
  if (typeof body !== "object" || body === null) {
    return { state: "unavailable", reason: "The boundary read returned no readable answer." };
  }
  const record = body as {
    ok?: unknown;
    reason?: unknown;
    root?: unknown;
    boundary?: {
      state?: unknown;
      reason?: unknown;
      members?: unknown;
      truncatedAt?: unknown;
      nextCursor?: unknown;
    };
  };
  if (record.ok !== true || typeof record.root !== "string" || record.boundary === undefined) {
    return {
      state: "unavailable",
      reason: typeof record.reason === "string" ? record.reason : "The boundary read was refused.",
    };
  }
  const boundary = record.boundary;
  const members = Array.isArray(boundary.members)
    ? boundary.members.flatMap((member: unknown): LocalBoundaryMember[] => {
        if (typeof member !== "object" || member === null) return [];
        const { id, label, kind, ancestors } = member as {
          id?: unknown;
          label?: unknown;
          kind?: unknown;
          ancestors?: unknown;
        };
        return typeof id === "string" && typeof label === "string"
          ? [
              {
                id,
                label,
                ...(kind === "member" || kind === "folder" ? { kind } : {}),
                ...(Array.isArray(ancestors) && ancestors.every((ref) => typeof ref === "string")
                  ? { ancestors }
                  : {}),
              },
            ]
          : [];
      })
    : [];
  if (boundary.state === "members") {
    return {
      state: "members",
      root: record.root,
      members,
      truncatedAt: null,
      reason: null,
      ...(typeof boundary.nextCursor === "string" || boundary.nextCursor === null
        ? { nextCursor: boundary.nextCursor }
        : {}),
    };
  }
  if (boundary.state === "overflow") {
    return {
      state: "members",
      root: record.root,
      members,
      truncatedAt: typeof boundary.truncatedAt === "number" ? boundary.truncatedAt : members.length,
      reason: typeof boundary.reason === "string" ? boundary.reason : null,
    };
  }
  return {
    state: "unavailable",
    reason:
      typeof boundary.reason === "string"
        ? boundary.reason
        : "The bound root is not readable on this machine.",
  };
}

/** Only a display cache. Selection and message authority live in the device's
 * existing command/event store; browsing never changes either. */
export interface LocalContextAddress {
  readonly environmentId: string;
  readonly threadId: string;
}
export type LocalSelectionRead = LocalContextSelection & { readonly selectionDigest: string };
export interface LocalBoundaryView {
  readonly state: "loading" | "ready" | "unavailable";
  readonly root: string | null;
  readonly selection: LocalSelectionRead | null;
  readonly pages: Readonly<Record<string, LocalBoundaryRead>>;
  readonly hasPreparedCopy?: boolean;
  readonly preparedCopy?: LocalBoundaryRead;
  readonly pending: number;
  readonly reason: string | null;
}
const entries = new Map<string, LocalBoundaryView>();
const listeners = new Set<() => void>();
const empty: LocalBoundaryView = {
  state: "loading",
  root: null,
  selection: null,
  pages: {},
  pending: 0,
  reason: null,
};
const keyOf = (address: LocalContextAddress) =>
  JSON.stringify([address.environmentId, address.threadId]);
const readView = (address: LocalContextAddress) => entries.get(keyOf(address)) ?? empty;
const publish = (address: LocalContextAddress, patch: Partial<LocalBoundaryView>) => {
  entries.set(keyOf(address), { ...readView(address), ...patch });
  listeners.forEach((listener) => listener());
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Refresh display paths by physical ancestry when a browsed folder moves.
 * Paths here are labels only; neither ids nor selection expressions change. */
export function refreshLocalBoundaryLabels(
  pages: Readonly<Record<string, LocalBoundaryRead>>,
  updates: ReadonlyArray<LocalBoundaryMember>,
): Record<string, LocalBoundaryRead> {
  const known = new Map(
    Object.values(pages).flatMap((page) =>
      page.state === "members" ? page.members.map((member) => [member.id, member] as const) : [],
    ),
  );
  const exact = new Map(updates.map((member) => [member.id, member]));
  const moved = updates
    .flatMap((member) => {
      const old = known.get(member.id);
      return member.kind === "folder" && old && old.label !== member.label
        ? [{ id: member.id, old: old.label, label: member.label }]
        : [];
    })
    .sort((a, b) => b.old.length - a.old.length);
  return Object.fromEntries(
    Object.entries(pages).map(([key, page]) => [
      key,
      page.state !== "members"
        ? page
        : {
            ...page,
            members: page.members.map((member) => {
              const replacement = exact.get(member.id);
              if (replacement) return replacement;
              const ancestor = moved.find(
                (folder) =>
                  member.ancestors?.includes(folder.id) &&
                  member.label.startsWith(`${folder.old}/`),
              );
              return ancestor
                ? { ...member, label: ancestor.label + member.label.slice(ancestor.old.length) }
                : member;
            }),
          },
    ]),
  );
}
async function requestLocal(
  address: LocalContextAddress,
  body: Record<string, unknown>,
  signal?: AbortSignal,
) {
  signal?.throwIfAborted();
  const response = await fetchPrimary("/api/local/boundary", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ threadId: address.threadId, ...body }),
    ...(signal ? { signal } : {}),
  });
  const result = (await response.json()) as Record<string, unknown>;
  signal?.throwIfAborted();
  if (!response.ok || result.ok !== true)
    throw new Error(
      typeof result.reason === "string"
        ? result.reason
        : "The Local context operation was refused.",
    );
  return result;
}
export async function refreshLocalSelection(
  address: LocalContextAddress,
  signal?: AbortSignal,
): Promise<LocalSelectionRead> {
  const result = await requestLocal(address, { operation: "selection" }, signal);
  if (
    result.environmentId !== address.environmentId ||
    result.threadId !== address.threadId ||
    typeof result.root !== "string"
  )
    throw new Error("The Local selection belongs to another device or conversation.");
  const selection = Schema.decodeUnknownSync(
    Schema.Struct({ ...LocalContextSelection.fields, selectionDigest: Schema.String }),
  )(result.selection);
  const current = readView(address).selection;
  if (current && current.selectionCut > selection.selectionCut) return current;
  publish(address, {
    state: "ready",
    root: result.root,
    selection,
    reason: null,
    hasPreparedCopy: result.hasPreparedCopy === true,
    ...(readView(address).root !== result.root ? { pages: {} } : {}),
  });
  return selection;
}
/** Subscribe to the existing device receipt without issuing another read. */
export function useLocalBoundarySnapshot(
  address: LocalContextAddress | null,
): LocalBoundaryView | null {
  return useSyncExternalStore(
    subscribe,
    () => (address === null ? null : readView(address)),
    () => (address === null ? null : empty),
  );
}
export function useLocalBoundary(address: LocalContextAddress | null): LocalBoundaryView | null {
  const key = address === null ? null : keyOf(address);
  const view = useLocalBoundarySnapshot(address);
  useEffect(() => {
    if (key === null) return;
    const [environmentId, threadId] = JSON.parse(key) as [string, string];
    const target = { environmentId, threadId };
    void refreshLocalSelection(target).catch((error) =>
      publish(target, {
        state: "unavailable",
        reason: error instanceof Error ? error.message : "The Local selection could not be read.",
      }),
    );
  }, [key]);
  return view;
}
export async function browseLocalDirectory(
  address: LocalContextAddress,
  directory: string,
  cursor?: string,
  source: "codebase" | "prepared-copy" = "codebase",
): Promise<void> {
  const sourceRoot = readView(address).root;
  const before =
    source === "prepared-copy"
      ? readView(address).preparedCopy
      : readView(address).pages[directory];
  if (before?.state === "loading") return;
  const setPage = (page: LocalBoundaryRead) => {
    if (source === "codebase" && readView(address).root !== sourceRoot) return;
    if (
      source === "codebase" &&
      page.state === "members" &&
      sourceRoot !== null &&
      page.root !== sourceRoot
    )
      return;
    if (source === "prepared-copy") {
      publish(address, { preparedCopy: page });
      return;
    }
    const current = readView(address).pages;
    const pages = refreshLocalBoundaryLabels(
      current[directory]?.state === "loading" && before
        ? { ...current, [directory]: before }
        : current,
      page.state === "members" ? page.members : [],
    );
    publish(address, { pages: { ...pages, [directory]: page } });
  };
  setPage({ state: "loading" });
  try {
    const page = projectLocalBoundaryResponse(
      await requestLocal(address, {
        operation: "browse",
        source,
        directory,
        ...(cursor ? { cursor } : {}),
      }),
    );
    const combined =
      page.state === "members" && cursor && before?.state === "members"
        ? { ...page, members: [...before.members, ...page.members] }
        : page;
    setPage(combined);
    if (source === "codebase" && directory === "" && !cursor) {
      // Names and ancestry only, bounded independently from selection readiness.
      // Retained choices remain removable if the source cannot resolve them.
      const expressions = readView(address).selection?.expressions ?? [];
      const references = expressions.flatMap((choice) => {
        const reference = parseFilesystemMemberReference(choice.memberId);
        return reference?.group === "root-codebase" ? [reference.relativePath] : [];
      });
      const named: LocalBoundaryMember[] = [];
      for (let offset = 0; offset < references.length; offset += 128) {
        const inspected = await requestLocal(address, {
          operation: "inspect",
          references: references.slice(offset, offset + 128),
        })
          .then(projectLocalBoundaryResponse)
          .catch(() => null);
        if (inspected?.state !== "members") break;
        named.push(...inspected.members);
      }
      if (named.length && readView(address).selection?.expressions === expressions) {
        const current = readView(address).pages[directory];
        if (current?.state === "members")
          setPage({ ...current, members: [...current.members, ...named] });
      }
    }
  } catch (error) {
    setPage({
      state: "unavailable",
      reason: error instanceof Error ? error.message : "The source could not be read.",
    });
  }
}
export function moveLocalContext(
  address: LocalContextAddress,
  move: {
    readonly expressions: ReadonlyArray<
      Omit<ContextSelectionExpression, "delivery"> & {
        readonly delivery?: ContextSelectionExpression["delivery"];
      }
    >;
    readonly replace?: boolean;
    readonly restoreFromResolutionId?: string;
    readonly codeRoot?: string;
    readonly clearGroups?: ReadonlyArray<string>;
  },
): Promise<void> {
  const shown = readView(address);
  if (!shown.selection)
    return Promise.reject(new Error("Wait for this conversation's selection to load."));
  const commandId = String(newCommandId());
  publish(address, { pending: shown.pending + 1, reason: null });
  return orderSessionSelection(address, shown.selection, async (order) => {
    try {
      if (order.failed || !order.expected)
        throw new Error("Review the preceding context change before trying again.");
      const predecessor = readView(address).selection;
      if (
        !predecessor ||
        predecessor.selectionCut !== order.expected.selectionCut ||
        predecessor.selectionDigest !== order.expected.selectionDigest
      )
        throw new Error(
          "The displayed selection changed. Review the current choices before trying again.",
        );
      const expectedSuccessor = order.expected.selectionCut + 1;
      const moved = await requestLocal(address, {
        operation: "move",
        command: {
          type: "thread.context.move",
          commandId,
          threadId: address.threadId,
          expectedCut: order.expected.selectionCut,
          ...move,
          expressions: move.expressions.map((expression) => ({
            ...expression,
            delivery: !expression.selected
              ? "reference"
              : (expression.delivery ??
                predecessor.expressions.find(
                  (previous) =>
                    previous.memberId === expression.memberId && previous.kind === expression.kind,
                )?.delivery ??
                "reference"),
          })),
          createdAt: new Date().toISOString(),
        },
      });
      const receipt = Schema.decodeUnknownSync(
        Schema.Struct({ ...LocalContextSelection.fields, selectionDigest: Schema.String }),
      )(moved.selection);
      if (receipt.selectionCut !== expectedSuccessor)
        throw new Error(
          "The selection changed in another client. Review the current choices before sending.",
        );
      const selection = await refreshLocalSelection(address);
      if (
        selection.selectionCut !== receipt.selectionCut ||
        selection.selectionDigest !== receipt.selectionDigest
      )
        throw new Error(
          "The selection changed in another client. Review the current choices before sending.",
        );
      order.accept(receipt);
    } catch (error) {
      order.refuse();
      await refreshLocalSelection(address).catch(() => undefined);
      publish(address, {
        reason: error instanceof Error ? error.message : "The context change failed.",
      });
      throw error;
    } finally {
      publish(address, { pending: Math.max(0, readView(address).pending - 1) });
    }
  });
}
export function prepareLocalSend(
  address: LocalContextAddress,
  messageId: string,
  createdAt: string,
): Promise<LocalContextResolution> {
  return orderSessionSelection(address, readView(address).selection ?? undefined, async (order) => {
    if (order.failed) throw new Error("Review the preceding context change before sending.");
    if (!order.expected) {
      await refreshLocalSelection(address);
      throw new Error("The selection has loaded. Review the current choices before sending again.");
    }
    try {
      const result = await requestLocal(address, {
        operation: "prepare",
        command: {
          type: "thread.context.prepare",
          commandId: `local-prepare-${messageId}`,
          threadId: address.threadId,
          messageId,
          expectedCut: order.expected.selectionCut,
          createdAt,
        },
      });
      const resolution = Schema.decodeUnknownSync(LocalContextResolution)(result.resolution);
      if (
        resolution.agentEnvironmentId !== address.environmentId ||
        resolution.agentThreadId !== address.threadId ||
        resolution.resolutionId !== `res-${messageId}` ||
        !agentContextResolutionDigestMatches(resolution)
      )
        throw new Error("The prepared Local message receipt does not match this Send.");
      return resolution;
    } catch (error) {
      order.refuse();
      await refreshLocalSelection(address).catch(() => undefined);
      throw error;
    }
  });
}
